"use client";

import { ApiClient, Option, isErr, isOk, isSome, none, some } from "@jace/api";
import { z } from "zod";
import { FileMangerBuilder } from "./common/factory";
import { parseUnifiedUri } from "./common/parse-unified-uri";
import { getDownloadUrl } from "./download/core";
import { DownloadSessionManager } from "./download/download";
import { SubjectHash } from "./hash";
import { Scheduler } from "./scheduler";
import { getTotalSessionSize } from "./session";
import { Storage } from "./storage/persist";
import {
	FileDownload,
	FileManagerConfiguration,
	InitialSessionData,
	Rollback,
	SessionType,
} from "./types";
import { executeUpload, getFileUploadUrl } from "./upload/core";
import { UploadSessionManager } from "./upload/upload";

type FileDownloadEventName = "start" | "update" | "success" | "error";

const fileDownloadSchema = z.string();

type BaseDownloadEvent = {
	progress: number;
};

type FileDownloadEventMap<TValue> = {
	start: BaseDownloadEvent & {
		type: "start";
		value: undefined;
	};
	update: BaseDownloadEvent & {
		type: "update";
		value: undefined;
	};
	success: BaseDownloadEvent & {
		type: "success";
		value: TValue;
	};
	error: BaseDownloadEvent & {
		type: "error";
		error: Error;
	};
};

type FileDownloadEvent<
	T extends FileDownloadEventName,
	TValue = unknown,
> = FileDownloadEventMap<TValue>[T];

type EventHandler<TEventName extends FileDownloadEventName, TValue> = (
	event: FileDownloadEvent<TEventName, TValue>,
) => void;

interface FileManagerRequest {
	uri: string;
	signal: AbortSignal;
	controller: AbortController;
}

const defaultConfig: FileManagerConfiguration = {
	uploads: {
		concurrent: 1,
		maxCount: 5,
		maxSize: 10 * 1024 * 1024, // 10MB
		multiple: true,
	},
	downloads: {
		concurrent: 5,
	},
};

export class FileManager {
	// TODO: Make this deep assignable
	public config: FileManagerConfiguration;
	public readonly threads = new Scheduler({ concurrency: 5 });
	public readonly storage = new Storage("localstorage");

	private inflight = new Map<
		string,
		{
			promise: Promise<void>;
			controller: AbortController;
		}
	>();

	private _session = new Map<
		string,
		UploadSessionManager | DownloadSessionManager
	>();

	constructor(
		public transport: ApiClient,
		config: Partial<FileManagerConfiguration> = {},
	) {
		this.config = Object.assign({}, defaultConfig, config);
	}

	public createSession<T extends SessionType>(
		subject: SubjectHash,
		type: T,
		initialData?: InitialSessionData<T>,
	): T extends SessionType.Upload
		? UploadSessionManager
		: DownloadSessionManager;
	public createSession<T extends SessionType>(
		subject: SubjectHash,
		type: T,
		initialData?: InitialSessionData<T>,
	): any {
		const exists = this.getSession(subject);

		if (isSome(exists)) {
			if (exists.value instanceof UploadSessionManager && initialData) {
				exists.value.init(initialData);
			}

			return exists.value;
		}

		if (type === SessionType.Upload) {
			const newSession = new UploadSessionManager(subject, this, {
				initialData,
			});
			this._session.set(subject.value, newSession);
			return newSession;
		}

		if (type === SessionType.Download) {
			const newSession = new DownloadSessionManager(subject, this);
			this._session.set(subject.value, newSession);
			return newSession;
		}

		throw new Error(`Unknown session type ${type}`);
	}

	public getSession(
		subject: SubjectHash,
	): Option<UploadSessionManager | DownloadSessionManager> {
		const existing = this._session.get(subject.value);

		if (existing) {
			return some(existing);
		}

		return none();
	}

	public resetSession(
		subject: SubjectHash | string | undefined,
	): Rollback | null {
		if (!subject) {
			return null;
		}

		const target = typeof subject === "string" ? subject : subject.value;
		const session = this._session.get(target);

		if (session && session instanceof UploadSessionManager) {
			session.reset();

			return () => {
				if (session) {
					this._session.set(target, session);
				}
			};
		}

		return null;
	}

	public deleteSession(
		subject: SubjectHash | string | undefined,
	): Rollback | null {
		if (!subject) {
			return null;
		}

		const target = typeof subject === "string" ? subject : subject.value;
		const session = this._session.get(target);

		this._session.delete(target);

		return () => {
			if (session) {
				this._session.set(target, session);
			}
		};
	}

	public async upload(file: File, subject: SubjectHash): Promise<void> {
		const session = this.getSession(subject).value?.session;

		if (!session) {
			return;
		}

		const total = getTotalSessionSize(session);

		if (total >= this.config.uploads.maxSize) {
			throw new Error(`Max uploads size reached at file ${file.name}`);
		}

		const next = FileMangerBuilder.upload(file, subject);

		const result = await this.threads.spawn(async () =>
			getFileUploadUrl(next, this.transport),
		);

		if (isOk(result)) {
			await this.threads.spawn(async () =>
				executeUpload(result.value, next, undefined, undefined, (data) => {
					console.log("change", data);
				}),
			);
		}

		// Manager response
	}

	public preload(uri: string, cb: (data: FileDownload) => void) {
		console.log("manager:preload", uri);
		this.threads.spawn(async () => {
			const metadata = parseUnifiedUri(uri);

			if (isOk(metadata)) {
				const { name, ext } = metadata.value;
				const data = FileMangerBuilder.download(uri, name, ext);

				cb(data);
			}
		});
	}

	public download(
		uri: string,
		handlers: {
			onStart?: EventHandler<"start", FileDownload>;
			onUpdate?: EventHandler<"update", FileDownload>;
			onSuccess?: EventHandler<"success", FileDownload>;
			onError?: EventHandler<"error", FileDownload>;
		} = {},
	): FileManagerRequest {
		const inflight = this.inflight.get(uri);

		if (inflight) {
			void inflight.promise;

			return {
				uri,
				signal: inflight.controller.signal,
				controller: inflight.controller,
			};
		}

		const promise = this.threads.spawn(async () => {
			const hit = await this.storage.get(uri, fileDownloadSchema);
			const parsingResult = parseUnifiedUri(uri);

			if (isErr(parsingResult)) {
				handlers.onError?.({
					type: "error",
					error: parsingResult.error,
					progress: 0,
				});
				return;
			}

			const { name, ext } = parsingResult.value;
			const download = FileMangerBuilder.download(uri, name, ext);

			if (hit.value) {
				download.source = hit.value;
				this.inflight.delete(uri);

				handlers.onSuccess?.({
					type: "success",
					value: download,
					progress: 1,
				});

				return;
			}

			// Cache miss branch
			const downloadUrlResult = await getDownloadUrl(uri, this.transport);

			this.inflight.delete(uri);

			if (isErr(downloadUrlResult)) {
				handlers.onError?.({
					type: "error",
					error: downloadUrlResult.error,
					progress: 0,
				});
				return;
			}

			// Store in cache
			download.source = downloadUrlResult.value.url;

			this.storage.set(uri, download.source);

			handlers.onSuccess?.({
				type: "success",
				value: download,
				progress: 1,
			});
		});

		const controller = new AbortController();

		this.inflight.set(uri, {
			promise,
			controller,
		});

		return {
			uri,
			signal: controller.signal,
			controller,
		};
	}

	//public delete(id: string, subject: SubjectHash) {}
}
